'use strict';
const get = require('lodash/object').get,
    {some} = require('lodash/collection'),
    {replace, trim} = require('lodash/string'),
    ContentUtils = require('./ContentUtils'),
    currencyFormatter = require('currency-formatter'),
    Constants = require('constants/AppConstants'),
    KEY_CURRENCY = 'cache_current_currency',
    KEY_LOCATION = 'cache_current_locator',
    numeral = require('numeral');
const PREFERRED_LANGUAGE = navigator.language || navigator.userLanguage || navigator.browserLanguage || navigator.systemLanguage || 'en';


/**
 * Helper class to handler the common/generic way the content in the browser, conditionals, cache handler,
 * queries element, cloning object, and other king of stuffs.
 */
const NumberUtils = {

    initializeFormat() {
        const currency = ContentUtils.getLocal(KEY_CURRENCY);
        if (numeral.locales['en'] === undefined) {
            numeral.register('locale', ContentUtils.getSession(KEY_LOCATION) || PREFERRED_LANGUAGE, {
                delimiters: {
                    thousands: get(currency, 'thousandsSeparator'),
                    decimal: get(currency, 'decimalSeparator')
                },
                currency: {
                    symbol: get(currency, 'symbol')
                }
            });
        }
    },

    isEqual(numOne, numTwo) {
        const numOneSafe = this.standardiseDecimalFormatNumber(removeText(numOne));
        const numTwoSafe = this.standardiseDecimalFormatNumber(removeText(numTwo));
        return numeral(numOneSafe).value() === numeral(numTwoSafe).value();
    },

    sum(numOne, numTwo) {
        const numOneSafe = this.standardiseDecimalFormatNumber(removeText(numOne));
        const numTwoSafe = this.standardiseDecimalFormatNumber(removeText(numTwo));
        return numeral(numOneSafe).add(numTwoSafe).value();
    },
    subtract(numOne, numTwo) {
        const numOneSafe = this.standardiseDecimalFormatNumber(removeText(numOne));
        const numTwoSafe = this.standardiseDecimalFormatNumber(removeText(numTwo));
        return numeral(numOneSafe).subtract(numTwoSafe).value();
    },

    multiply(numOne, numTwo) {
        const numOneSafe = this.standardiseDecimalFormatNumber(removeText(numOne));
        const numTwoSafe = this.standardiseDecimalFormatNumber(removeText(numTwo));
        return numeral(numOneSafe).multiply(numTwoSafe).value();
    },

    multiplyAndTruncate(numOne, numTwo, truncateDecimal = 2) {
        const numOneSafe = this.standardiseDecimalFormatNumber(removeText(numOne));
        const numTwoSafe = this.standardiseDecimalFormatNumber(removeText(numTwo));
        return this.truncate(numeral(numOneSafe).multiply(numTwoSafe).value(), truncateDecimal);
    },

    divide(numOne = 1, numTwo = 1) {
        const numOneSafe = this.standardiseDecimalFormatNumber(removeText(numOne));
        const numTwoSafe = this.standardiseDecimalFormatNumber(removeText(numTwo));
        return numeral(numOneSafe).divide(numTwoSafe).value();
    },

    divideAndTruncate(numOne, numTwo, truncateDecimal = 2) {
        const numOneSafe = this.standardiseDecimalFormatNumber(removeText(numOne));
        const numTwoSafe = this.standardiseDecimalFormatNumber(removeText(numTwo));
        return this.truncate(numeral(numOneSafe).divide(numTwoSafe).value(), truncateDecimal);
    },

    isGreater(numOne, numTwo) {
        numOne = +this.standardiseDecimalFormatNumber(numOne);
        numTwo = +this.standardiseDecimalFormatNumber(numTwo);

        return numOne > numTwo;
    },

    isGreaterOrEquals(numOne, numTwo) {
        return this.isEqual(numOne, numTwo) || this.isGreater(numOne, numTwo);
    },

    isLess(numOne, numTwo) {
        numOne = +this.standardiseDecimalFormatNumber(numOne);
        numTwo = +this.standardiseDecimalFormatNumber(numTwo);

        return numOne < numTwo;
    },

    isLessOrEquals(numOne, numTwo) {
        return this.isEqual(numOne, numTwo) || this.isLess(numOne, numTwo);
    },

    isNumber(number) {
        const saveNumber = number.replace(',', '').replace('.', '');
        return !isNaN(parseFloat(saveNumber)) && isFinite(saveNumber);
    },

    containSomeNotNumber(number) {
        const safeNumber = String(number);
        return !!safeNumber.match(/^[0-9]*$/gm) === false;
    },
    /**
     * Remove item duplicate, checking equality by given attributes.
     * @param array
     * @param props
     * @return {*}
     */
    removeDuplicateByAttribute(array, props) {

        if (get(array, 'length', 0) === 0 || get(props, 'length', 0) === 0) {
            return array;
        }

        let nonDuplicates = [];

        for (const obj of array) {
            const isDuplicate = some(nonDuplicates, target => ContentUtils.isSameObject(target, obj, props));
            if (!isDuplicate) {
                nonDuplicates.push(obj);
            }
        }

        return nonDuplicates;
    },

    /**
     * Retrieve in safe way the number of given string
     * @param order
     * @return {number}
     */
    getNumber(number, defaultResult = 0) {
        if (typeof number === 'number') {
            return number;
        }
        if (!number) {
            return defaultResult;
        }

        if (number && typeof number === 'string' && this.isAbleToCastToNumber(number)){
            return +number;
        }

        const currencyInfo = this.getCurrentCurrency();
        const currentThousands = get(currencyInfo, 'thousandsSeparator');


        if (number && number.replace) {
            if (number.indexOf(currentThousands) > -1) {
                number = number.replace(currentThousands, '');
            }

            if (this.isAbleToCastToNumber(number)) {
                return +number;
            }
        }

        if (number) {
            return +this.standardiseDecimalFormatNumber(number);
        } else {
            return defaultResult;
        }
    },

    isAbleToCastToNumber(value) {
        return 'NaN' !== String(+value);
    },

    /**
     * Retrieve in safe way the number text of given string
     * which support decimal and dot thousand numbers.
     * @param order
     * @return {String}
     */
    getNumberString(number, defaultResult = '0') {
        const isInvalid = +number === 0 || String(number) === 'NaN';

        if (isInvalid) {
            return number;
        }

        if (!number) {
            return defaultResult;
        }
        if (number && number.match) {
            return number.match(/[\d.,]/gi).join('');
        } else {
            return defaultResult;
        }
    },

    round(value, precision) {
        const multiplier = Math.pow(10, precision || 0);
        return Math.round(value * multiplier) / multiplier;
    },

    inverseSign(number) {
        const safeNumber = String(number);
        const isNegative = safeNumber.includes('-');
        return +(isNegative ? safeNumber.substring(1) : `-${safeNumber}`);
    },

    toNumber(number) {
        return this.getNumber(number);
    },

    /**
     * Retrieve the object of given string content.
     * @param {String} content The content that will be parsed
     * @param {String} field (Optionally)
     * @return {Object}
     */
    parse(content, field) {
        if (!content) {
            return content;
        }

        try {
            if (field) {
                const resultContent = get(content, field);
                return typeof resultContent === 'string' ? JSON.parse(resultContent) : resultContent;
            } else {
                return typeof content === 'string' ? JSON.parse(content) : content;
            }
        } catch (e) {
            console.error(e.message);
            return content;
        }
    },

    formatDecimal(number, decimalDigits = 2) {
        if (typeof decimalDigits === 'string') {
            decimalDigits = +decimalDigits;
        }
        const currencyInfo = this.getCurrentCurrency();
        return trim(currencyFormatter.format(this.standardiseDecimalFormatNumber(number), {
            code: get(currencyInfo, 'code'),
            precision: decimalDigits || get(currencyInfo, 'decimalDigits'),
            decimalDigits: decimalDigits || get(currencyInfo, 'decimalDigits'),
            symbolOnLeft: false,
            symbol: '',
            spaceBetweenAmountAndSymbol: false
        }));
    },

    /**
     * This method allow us to convert specific decimal number to an
     * generic formatted. This are usefully when need persist in a generic
     * way one number. For instance, if the user want to change the decimal
     * format of APP to another different decimal numbers.
     *
     * @param {String|Number} number
     * @return {String}
     */
    standardiseDecimalFormatNumber(number) {

        const isInvalid = number === null || number === undefined || +number === 0 || String(number) === 'NaN';

        if (isInvalid) {
            return number;
        }

        // convert to string in order to avoid problems with `replace` and `,` character.
        let safeNumber = String(number);

        const currencyInfo = this.getCurrentCurrency();
        const currentDecimalSeparator = get(currencyInfo, 'decimalSeparator');
        const currentThousands = get(currencyInfo, 'thousandsSeparator');

        const haveDecimalSeparator = String(number).includes(currentDecimalSeparator);
        if (!haveDecimalSeparator) {
            // enter here means we don't need to standardise nothing
            // because don't found any particular decimal separator
            return safeNumber;
        }

        // remove thousands character in order to be formatted
        // again this character for their needed.
        safeNumber = safeNumber.split(currentThousands).join('');

        // change the decimal separator of this specific currency to a generic one
        safeNumber = replace(safeNumber, currentDecimalSeparator, Constants.STANDARDISE_DECIMAL_SEPARATOR);

        return trim(safeNumber);
    },

    isStandardizedNumber(number) {
        const isInvalid = +number === 0 || String(number) === 'NaN';

        if (isInvalid) {
            return number;
        }

        const safeNumber = String(number);

        if (safeNumber.includes(',')) {
            return false;
        }

        if (!safeNumber.includes('.')) {
            return true;
        }

        if (safeNumber.match(/[.]/gi).length === 1) {
            return true;
        }

        return false;
    },

    formatCurrency(number, decimalDigits = 4) {
        const currencyInfo = this.getCurrentCurrency();
        return currencyFormatter.format(number, {
            code: get(currencyInfo, 'code'),
            decimalDigits: decimalDigits || get(currencyInfo, 'decimalDigits'),
            precision: decimalDigits || get(currencyInfo, 'decimalDigits')
        });
    },

    getCurrentCurrency() {
        return ContentUtils.getLocal(KEY_CURRENCY);
    },

    truncate(text, digits = 2) {
        const re = new RegExp('(\\d+\\.\\d{' + digits + '})(\\d)'),
            m = String(text).match(re);
        return String(m ? parseFloat(m[1]) : text.valueOf());
    }
};

/**
 * Filter obj and return only numbers which and `.` character
 * (which represent a generic decimal separator)
 *
 * @param num
 * @return {string}
 */
function removeText(num) {

    if (num === 'Infinity' || num === Infinity || num === undefined || num === '' || num === null) {
        return '0';
    }

    return (num ? String(num).match(/[\d,.]/gi).join('') : num) || '0';
}

module.exports = NumberUtils;



/** WEBPACK FOOTER **
 ** ./src/utils/NumberUtils.js
 **/